import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import StockTransferController from './controllers/stock-transfer-controller';
import DepotController from './controllers/depot-controller';
import CaisseController from './controllers/caisse-controller';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

const StockTransfersPage = () => {
  const { hasPermission } = usePermissionViewModel('transfert');
  const canWrite = hasPermission('write');
  const [loading, setLoading] = useState(true);
  const [transfers, setTransfers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [depots, setDepots] = useState([]);
  const [filterSource, setFilterSource] = useState('');
  const [filterDest, setFilterDest] = useState('');

  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    DepotController.getAllDepots()
      .then((list) => setDepots(list || []))
      .catch(() => setDepots([]));
  }, []);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSource) params.sourceDepotId = filterSource;
      if (filterDest) params.destinationDepotId = filterDest;
      const r = await StockTransferController.paginate(page, pageSize, params);
      setTransfers(r?.list || []);
      setTotalItems(r?.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement des transferts');
    } finally {
      setLoading(false);
    }
  }, [page, filterSource, filterDest]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const maxPage = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Transferts de stock</h1>
        <button
          onClick={() => setShowCreate(true)}
          disabled={!canWrite}
          className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title={canWrite ? '' : 'Vous n’avez pas la permission'}
        >
          + Nouveau transfert
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Dépôt source</label>
          <select
            value={filterSource}
            onChange={(e) => { setPage(1); setFilterSource(e.target.value); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          >
            <option value="">Tous</option>
            {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Dépôt destination</label>
          <select
            value={filterDest}
            onChange={(e) => { setPage(1); setFilterDest(e.target.value); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
          >
            <option value="">Tous</option>
            {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <button
          onClick={() => { setFilterSource(''); setFilterDest(''); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Réinitialiser
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Référence</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Destination</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Lignes</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Qté totale</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Chargement…</td></tr>
              ) : transfers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Aucun transfert.</td></tr>
              ) : transfers.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{t.reference || `#${t.id}`}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{fmtDate(t.dateTransfert)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t.sourceDepotName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t.destinationDepotName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t.utilisateurName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{t.lineCount || 0}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{t.totalQuantite || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalItems > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50"
            >
              ← Précédent
            </button>
            <span className="text-sm text-gray-600">Page {page} / {maxPage} • {totalItems} résultat(s)</span>
            <button
              disabled={page >= maxPage || loading}
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50"
            >
              Suivant →
            </button>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTransferModal
          depots={depots}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); fetchTransfers(); }}
        />
      )}
    </div>
  );
};

const CreateTransferModal = ({ depots, onClose, onSaved }) => {
  const [sourceDepotId, setSourceDepotId] = useState('');
  const [destDepotId, setDestDepotId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([]); // [{productId, productVariantId, name, stock, quantite}]
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!sourceDepotId) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await CaisseController.searchProducts(1, 15, search, sourceDepotId);
        setResults((r?.list || []).slice().sort((a, b) => (b.stock || 0) - (a.stock || 0)));
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, sourceDepotId]);

  const addLine = (item) => {
    const key = `${item.productId}-${item.productVariantId || 0}`;
    if (lines.some((l) => `${l.productId}-${l.productVariantId || 0}` === key)) {
      toast.info('Produit déjà ajouté');
      return;
    }
    setLines((prev) => [...prev, {
      productId: item.productId,
      productVariantId: item.productVariantId,
      name: item.name,
      stock: item.stock,
      quantite: 1,
    }]);
  };

  const updateLineQty = (idx, qty) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, quantite: qty } : l));
  };

  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!sourceDepotId || !destDepotId) { toast.error('Sélectionnez les dépôts'); return; }
    if (String(sourceDepotId) === String(destDepotId)) { toast.error('Source et destination doivent être différents'); return; }
    if (lines.length === 0) { toast.error('Ajoutez au moins une ligne'); return; }
    for (const l of lines) {
      const q = parseInt(l.quantite, 10);
      if (isNaN(q) || q <= 0) { toast.error(`Quantité invalide pour ${l.name}`); return; }
      if (q > l.stock) { toast.error(`Stock insuffisant pour ${l.name} (dispo: ${l.stock})`); return; }
    }
    setSaving(true);
    try {
      await StockTransferController.save(
        {
          sourceDepotId: Number(sourceDepotId),
          destinationDepotId: Number(destDepotId),
          dateTransfert: new Date().toISOString(),
          notes: notes || null,
        },
        lines.map((l) => ({
          productId: l.productId,
          productVariantId: l.productVariantId || null,
          quantite: parseInt(l.quantite, 10),
        }))
      );
      toast.success('Transfert enregistré');
      onSaved();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Nouveau transfert de stock</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white text-xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Dépôt source</label>
              <select
                value={sourceDepotId}
                onChange={(e) => { setSourceDepotId(e.target.value); setLines([]); }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
              >
                <option value="">— Sélectionner —</option>
                {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Dépôt destination</label>
              <select
                value={destDepotId}
                onChange={(e) => setDestDepotId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
              >
                <option value="">— Sélectionner —</option>
                {depots.filter((d) => String(d.id) !== String(sourceDepotId)).map((d) =>
                  <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optionnel)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Rechercher un produit / variante (du dépôt source)</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!sourceDepotId}
              placeholder={sourceDepotId ? 'Nom, référence, code-barres…' : 'Sélectionnez d\'abord un dépôt source'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 disabled:bg-gray-100"
            />
            {sourceDepotId && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {searching ? (
                  <div className="p-3 text-sm text-gray-500 text-center">Recherche…</div>
                ) : results.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">Aucun produit.</div>
                ) : results.map((r) => (
                  <button
                    key={`${r.productId}-${r.productVariantId || 0}`}
                    onClick={() => addLine(r)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                  >
                    <span>
                      <span className="font-medium text-gray-900">{r.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{r.reference}</span>
                    </span>
                    <span className={`text-xs font-semibold ${r.stock > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      Stock: {r.stock}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Lignes à transférer</label>
            {lines.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center border border-dashed border-gray-300 rounded-lg">
                Aucune ligne ajoutée.
              </div>
            ) : (
              <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Produit</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Stock source</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Quantité</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lines.map((l, idx) => (
                    <tr key={`${l.productId}-${l.productVariantId || 0}`}>
                      <td className="px-3 py-2 text-gray-900">{l.name}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{l.stock}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={1}
                          max={l.stock}
                          value={l.quantite}
                          onChange={(e) => updateLineQty(idx, e.target.value)}
                          className="w-20 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => removeLine(idx)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          Retirer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : 'Valider le transfert'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockTransfersPage;
